import React, { useMemo, useCallback } from 'react';
import { Icon } from 'components/atoms';

export interface HistoryItemProps {
  documentId: string;
  conversationId?: string;
  filename: string;
  status: string;
  createdAt: string;
  isActive?: boolean;
  onClick?: (documentId: string, conversationId?: string) => void;
  className?: string;
}

const HistoryItem: React.FC<HistoryItemProps> = React.memo(({
  documentId,
  conversationId,
  filename,
  status,
  isActive = false,
  onClick,
  className = '',
}) => {
  const baseClasses =
    'w-full flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all text-sm';

  const activeClasses = useMemo(
    () =>
      isActive
        ? 'bg-teal-50 text-teal-700'
        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
    [isActive]
  );

  const statusIcon = useMemo(() => {
    switch (status) {
      case 'completed':
        return 'check';
      case 'processing':
      case 'pending':
        return 'clock';
      case 'failed':
        return 'x';
      default:
        return 'document';
    }
  }, [status]);

  const statusColor = useMemo(() => {
    switch (status) {
      case 'completed':
        return 'text-green-500';
      case 'processing':
      case 'pending':
        return 'text-yellow-500';
      case 'failed':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  }, [status]);

  const handleClick = useCallback(() => {
    if (onClick) {
      onClick(documentId, conversationId);
    }
  }, [onClick, documentId, conversationId]);

  // Memoize truncated filename
  const truncatedFilename = useMemo(
    () => (filename.length > 25 ? `${filename.substring(0, 22)}...` : filename),
    [filename]
  );

  return (
    <button
      className={`${baseClasses} ${activeClasses} ${className}`}
      onClick={handleClick}
      type="button"
      title={filename}
    >
      <Icon name={statusIcon} className={`w-4 h-4 flex-shrink-0 ${statusColor}`} />
      <span className="font-normal truncate flex-1 text-left">{truncatedFilename}</span>
    </button>
  );
});

HistoryItem.displayName = 'HistoryItem';

export default HistoryItem;
